import type { MessageDescriptor } from '@lingui/core';
import type { ComponentType } from 'react';
import type { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

import type { DescriptionItem } from '@/shared/record';

/** Chave do passo na URL (`?passo=pessoa`, U39 §2): F5 e o voltar do navegador seguem o fluxo. */
export const FLOW_PARAM = 'passo';

const STEP_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const OVERLAY_KEY = 'sheet';

export interface FlowStepContext<V extends FieldValues> {
  readonly form: UseFormReturn<V>;
  readonly signal: AbortSignal;
}

export interface FlowBackContext<V extends FieldValues> {
  readonly form: UseFormReturn<V>;
  /** O passo para onde se volta. */
  readonly to: string;
}

/** Ícone de etapa no trilho (Lucide ou compatível). */
export type FlowIcon = ComponentType<{ readonly className?: string; readonly 'aria-hidden'?: boolean | 'true' }>;

/** Um passo do fluxo (arquétipo Fluxo, U39 §1). */
export interface FlowStep<V extends FieldValues> {
  /** Slug em pt-BR, o que vai na URL (`pessoa`, `simular`). */
  readonly id: string;
  readonly title: MessageDescriptor | string;
  readonly description?: MessageDescriptor | string;
  /** Campos validados ao avançar (`form.trigger`); um erro de API nesses campos volta a este passo. */
  readonly fields?: readonly FieldPath<V>[];
  /** Passo condicional: some do progresso e da navegação quando devolve `false`. */
  readonly when?: (values: V) => boolean;
  /** `review`: o último passo, cujo botão principal pede a confirmação. */
  readonly kind?: 'form' | 'review';
  /** Rótulo do botão principal ("Aprovar proposta"); padrão "Continuar" (ou o da confirmação na revisão). */
  readonly nextLabel?: MessageDescriptor | string;
  /** Rótulo do voltar ("Voltar para simulação"); padrão "Voltar". */
  readonly backLabel?: MessageDescriptor | string;
  /**
   * Trabalho do passo antes de avançar (a simulação do passo "Simular" no legado). Lançar mostra o
   * erro no passo (campos da API no campo); devolver `false` fica sem mensagem.
   */
  readonly beforeNext?: (values: V, context: FlowStepContext<V>) => unknown;
  /**
   * Quantos passos visíveis o "Voltar" deste passo recua (padrão 1). O refinanciamento do legado volta
   * dos Detalhes aos contratos pulando a simulação (PayrollCreditNoteContainer.tsx:81-89).
   */
  readonly backSteps?: number | ((values: V) => number);
  /**
   * Limpa dados ao voltar deste passo ("Voltar" da tela ou do navegador; não roda no "Editar" nem no
   * clique do progresso). Depois dele, o passo de destino e os seguintes deixam de estar concluídos. Car
   * Equity: zerar CPF e pessoa ao voltar do Veículo e os overrides ao voltar das Parcelas
   * (CarEquityContainer.tsx:52-72).
   */
  readonly onBack?: (values: V, context: FlowBackContext<V>) => void;
  /** Ícone da etapa no trilho vertical (Fluxo guiado, U53). */
  readonly icon?: FlowIcon;
  /**
   * O que a etapa decidiu, para o trilho, o painel "Suas escolhas" e o Resumo (as mesmas linhas, U53). Só lido
   * com a etapa concluída; vazio fica "a definir".
   */
  readonly summary?: (values: V) => readonly DescriptionItem[];
  /**
   * Etapa de escolha única em cards: a escolha explícita (clique, Enter, Espaço; as setas não) avança sozinha,
   * depois do ✓ (W07). Precisa de exatamente um campo em `fields`.
   */
  readonly autoAdvance?: boolean;
  /**
   * `false`: o rascunho recusa o dado desta etapa (documento): depois do F5 ela e as seguintes voltam a fazer,
   * em vez de reabrir um passo adiante sem o dado que ele pressupõe.
   */
  readonly draft?: boolean;
}

/**
 * Etapa anterior ao fluxo, concluída pelo caminho da URL (produto e modalidade do Contratar, W02): entra no trilho,
 * no painel e no Resumo; "Alterar" sai do fluxo pela navegação (o guarda pergunta se há alteração).
 */
export interface FlowLeadingStep {
  readonly id: string;
  readonly title: MessageDescriptor | string;
  readonly icon?: FlowIcon;
  /** O que foi escolhido ("Consignado público"). */
  readonly value: string;
  /** Volta à escolha. */
  readonly onSelect?: () => void;
}

export interface FlowDefinition<V extends FieldValues> {
  /** `domínio.nome`, como o id de bloco e de tela. */
  readonly id: string;
  /** Chave da URL; padrão `passo`. */
  readonly param?: string;
  readonly steps: readonly FlowStep<V>[];
}

const FLOW_ID = /^[a-z][a-zA-Z0-9-]*\.[a-z][a-zA-Z0-9-.]*$/;

/** Problemas da definição: id, slugs únicos, ao menos dois passos, revisão só no fim. */
export function validateFlow<V extends FieldValues>(flow: FlowDefinition<V>): string[] {
  const owner = `fluxo ${flow.id}`;
  const issues: string[] = [];
  if (!FLOW_ID.test(flow.id)) issues.push(`${owner}: id inválido (use domínio.nome)`);
  if (flow.param !== undefined && !/^[a-z][a-zA-Z]*$/.test(flow.param)) issues.push(`${owner}: param inválido`);
  if (flow.param === OVERLAY_KEY) issues.push(`${owner}: param não pode ser "${OVERLAY_KEY}" (é do overlay)`);
  if (flow.steps.length < 2) issues.push(`${owner}: declare ao menos dois passos`);
  const seen = new Set<string>();
  flow.steps.forEach((step, index) => {
    if (!STEP_ID.test(step.id)) issues.push(`${owner}: passo "${step.id}" com id inválido (slug minúsculo)`);
    if (seen.has(step.id)) issues.push(`${owner}: passo "${step.id}" repetido`);
    seen.add(step.id);
    if (step.kind === 'review' && index !== flow.steps.length - 1) issues.push(`${owner}: a revisão "${step.id}" precisa ser o último passo`);
    if (step.kind === 'review' && step.when) issues.push(`${owner}: a revisão "${step.id}" não pode ser condicional`);
    if (typeof step.backSteps === 'number' && (!Number.isInteger(step.backSteps) || step.backSteps < 1)) issues.push(`${owner}: passo "${step.id}" com backSteps inválido (inteiro ≥ 1)`);
    if (step.autoAdvance && (step.kind === 'review' || step.fields?.length !== 1)) issues.push(`${owner}: passo "${step.id}" com autoAdvance precisa de exatamente um campo e não pode ser a revisão`);
  });
  if (flow.steps[0]?.when) issues.push(`${owner}: o primeiro passo não pode ser condicional`);
  return issues;
}

/** Declara um fluxo. Em dev, definição inválida lança erro; em produção só devolve o objeto. */
export function defineFlow<V extends FieldValues>(flow: FlowDefinition<V>): FlowDefinition<V> {
  if (import.meta.env.DEV) {
    const issues = validateFlow(flow);
    if (issues.length > 0) throw new Error(issues.join('; '));
  }
  return flow;
}

/** Os passos que valem para estes valores (`when`), na ordem da definição. */
export function visibleSteps<V extends FieldValues>(flow: FlowDefinition<V>, values: V): readonly FlowStep<V>[] {
  return flow.steps.filter((step) => step.when?.(values) ?? true);
}

/** Um passo pode ser aberto quando todos os visíveis antes dele foram concluídos. */
export function isReachable(steps: readonly { readonly id: string }[], id: string, completed: ReadonlySet<string>): boolean {
  const index = steps.findIndex((step) => step.id === id);
  if (index < 0) return false;
  return steps.slice(0, index).every((step) => completed.has(step.id));
}

/** O primeiro passo visível ainda não concluído (ou o último, se todos foram). */
export function firstIncomplete<S extends { readonly id: string }>(steps: readonly S[], completed: ReadonlySet<string>): S | undefined {
  return steps.find((step) => !completed.has(step.id)) ?? steps.at(-1);
}

/** Quantos passos o "Voltar" deste passo recua para estes valores (inteiro ≥ 1). */
export function backStepsOf<V extends FieldValues>(step: FlowStep<V>, values: V): number {
  const raw = typeof step.backSteps === 'function' ? step.backSteps(values) : (step.backSteps ?? 1);
  return Number.isFinite(raw) ? Math.max(1, Math.trunc(raw)) : 1;
}

/** O passo dono de um campo: o caminho é o campo ou está dentro dele (`address.zip` em `address`). */
export function stepOfField<V extends FieldValues>(steps: readonly FlowStep<V>[], name: string): FlowStep<V> | undefined {
  return steps.find((step) => step.fields?.some((field) => name === field || name.startsWith(`${field}.`) || field.startsWith(`${name}.`)));
}

/** A busca do fluxo, para o `validateSearch` da rota (junte ao schema da tela). */
export function flowSearchSchema(param: string = FLOW_PARAM) {
  return z.object({ [param]: z.string().regex(STEP_ID).optional().catch(undefined) });
}

function withoutKeys(search: Readonly<Record<string, unknown>>, keys: readonly string[]) {
  return Object.fromEntries(Object.entries(search).filter(([key]) => !keys.includes(key)));
}

/**
 * `leaves` do `useFormScreen` para um fluxo: trocar de passo (ou abrir um sheet) não sai do formulário;
 * mudar o caminho ou outra chave da busca sai, e o guarda pergunta se há alteração.
 */
export function flowLeaves(param: string = FLOW_PARAM) {
  return (current: { readonly pathname: string; readonly search: Readonly<Record<string, unknown>> }, next: { readonly pathname: string; readonly search: Readonly<Record<string, unknown>> }) =>
    current.pathname !== next.pathname || JSON.stringify(withoutKeys(current.search, [param, OVERLAY_KEY])) !== JSON.stringify(withoutKeys(next.search, [param, OVERLAY_KEY]));
}
